import { useState, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X, Loader2, Search, Check } from 'lucide-react';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Optional secondary line rendered under the label (e.g. floor name). */
  description?: string;
  /** Optional extra search terms that are matched but not displayed. */
  keywords?: string[];
}

interface MultiSelectProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  loading?: boolean;
  label?: React.ReactNode;
  error?: React.ReactNode;
  className?: string;
  id?: string;
  emptyText?: string;
  /** Maximum number of chips shown in the trigger before collapsing into "+N more". */
  maxVisibleChips?: number;
}

export function MultiSelect({
  values,
  onChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  disabled = false,
  loading = false,
  label,
  error,
  className = '',
  id,
  emptyText = 'No options available',
  maxVisibleChips = 3,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const selectedOptions = useMemo(
    () => values.map((v) => options.find((o) => o.value === v)).filter(Boolean) as MultiSelectOption[],
    [options, values],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const haystack = `${o.label} ${o.description || ''} ${(o.keywords || []).join(' ')}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      document.addEventListener('keydown', handleKey);
      return () => {
        document.removeEventListener('mousedown', handleClick);
        document.removeEventListener('keydown', handleKey);
      };
    }
  }, [open]);

  const updatePosition = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
  };

  useLayoutEffect(() => {
    if (open) {
      updatePosition();
      const handleResize = () => updatePosition();
      const handleScroll = () => updatePosition();
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [open]);

  const toggle = (val: string) => {
    onChange(values.includes(val) ? values.filter((v) => v !== val) : [...values, val]);
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const visibleChips = selectedOptions.slice(0, maxVisibleChips);
  const hiddenCount = selectedOptions.length - visibleChips.length;

  const { highlightedIndex, listRef, onKeyDown, getItemProps } = useListKeyboardNav({
    itemCount: filtered.length,
    onSelect: (index) => {
      const opt = filtered[index];
      if (opt) toggle(opt.value);
    },
    resetKey: query,
  });

  const dropdown = open ? (
    <div
      style={dropdownStyle}
      className="bg-surface border border-border rounded-xl shadow-xl max-h-72 flex flex-col overflow-hidden"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="p-2 border-b border-border">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={searchPlaceholder}
            className="w-full pl-8 pr-2 py-1.5 text-sm bg-background border border-border rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>
      <div ref={listRef} className="overflow-y-auto flex-1 p-1">
        {filtered.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-text-muted">
            {options.length === 0 ? emptyText : 'No results found'}
          </div>
        ) : (
          filtered.map((opt, idx) => {
            const checked = values.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                {...getItemProps(idx)}
                onClick={() => toggle(opt.value)}
                className={`w-full flex items-start gap-2.5 text-left px-2.5 py-2 rounded-lg transition-colors ${
                  checked
                    ? 'bg-primary/10'
                    : idx === highlightedIndex
                      ? 'bg-background'
                      : 'hover:bg-background'
                }`}
              >
                <span
                  className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                    checked ? 'bg-primary border-primary text-white' : 'border-border bg-surface'
                  }`}
                >
                  {checked && <Check size={11} strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-sm ${checked ? 'text-primary font-medium' : 'text-text'}`}>
                    {opt.label}
                  </span>
                  {opt.description && (
                    <span className="block truncate text-xs mt-0.5 text-text-muted">{opt.description}</span>
                  )}
                </span>
              </button>
            );
          })
        )}
      </div>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border bg-background/40">
        <span className="text-xs text-text-muted">
          {values.length} selected
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onChange(Array.from(new Set([...values, ...filtered.map((o) => o.value)])))}
            className="text-xs font-medium text-primary hover:underline"
          >
            Select all
          </button>
          {values.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs font-medium text-text-muted hover:text-text"
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div ref={containerRef} className="relative w-full">
      {label && <label className={typeof label === 'string' ? 'text-sm font-medium text-text' : ''}>{label}</label>}
      <button
        type="button"
        id={id}
        disabled={disabled || loading}
        onClick={() => !disabled && !loading && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (!disabled && !loading && !open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={`mt-2 w-full flex items-center gap-2 min-h-[38px] px-3 py-1.5 border rounded-lg bg-background text-sm transition-colors focus:outline-none focus:ring-2 ${
          disabled || loading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
        } ${error ? 'border-danger focus:ring-danger/30' : 'border-border focus:ring-primary/30'} ${className}`}
      >
        {loading ? (
          <span className="flex items-center gap-2 text-text-muted">
            <Loader2 size={14} className="animate-spin" /> Loading...
          </span>
        ) : selectedOptions.length === 0 ? (
          <span className="text-text-muted">{placeholder}</span>
        ) : (
          <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
            {visibleChips.map((opt) => (
              <span
                key={opt.value}
                title={opt.label}
                className="inline-flex max-w-[10rem] items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
              >
                <span className="truncate">{opt.label}</span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(opt.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      toggle(opt.value);
                    }
                  }}
                  className="rounded hover:bg-primary/20"
                >
                  <X size={11} strokeWidth={3} />
                </span>
              </span>
            ))}
            {hiddenCount > 0 && (
              <span className="shrink-0 rounded-md bg-background border border-border px-2 py-0.5 text-xs font-medium text-text-muted">
                +{hiddenCount} more
              </span>
            )}
          </span>
        )}
        <ChevronDown
          size={15}
          className={`ml-auto shrink-0 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {dropdown && createPortal(dropdown, document.body)}
      {error}
    </div>
  );
}
